/**
 * HexMapMockup v2 — Hybrid hex adventure map prototype
 *
 * Uses three tile layers:
 *   1. Top-down base tiles for seamless interior terrain
 *   2. Feature overlays (transparent bg) layered on top
 *   3. Isometric edge tiles for map perimeter with 3D depth
 *
 * Route: /dev/hex-map
 */

import React, { useState, useCallback } from 'react';

// ─── Tile Assets ──────────────────────────────────────────────────────────────

const TILES = {
  // Top-down base tiles (seamless, fill entire hex)
  td: {
    grass:      '/mockup-tiles/td-grass-lush.png',
    grassDry:   '/mockup-tiles/td-grass-dry.png',
    dirt:       '/mockup-tiles/td-dirt-road.png',
    cobble:     '/mockup-tiles/td-cobblestone.png',
    water:      '/mockup-tiles/td-water-shallow.png',
    waterDeep:  '/mockup-tiles/td-water-deep.png',
    sand:       '/mockup-tiles/td-sand-desert.png',
    swamp:      '/mockup-tiles/td-swamp-mud.png',
    snow:       '/mockup-tiles/td-snow.png',
    lava:       '/mockup-tiles/td-lava-surface.png',
    ruins:      '/mockup-tiles/td-stone-ruins.png',
    crystal:    '/mockup-tiles/td-crystal-floor.png',
  },
  // Isometric edge tiles (3D cliff depth for perimeter)
  edge: {
    grass:  '/mockup-tiles/iso-edge-cliff-grass.png',
    snow:   '/mockup-tiles/iso-edge-cliff-snow.png',
    desert: '/mockup-tiles/iso-edge-cliff-desert.png',
    lava:   '/mockup-tiles/iso-edge-cliff-lava.png',
  },
  // Feature overlays (transparent bg, sit on top of base)
  overlay: {
    pines:     '/mockup-tiles/overlay-pine-trees.png',
    oaks:      '/mockup-tiles/overlay-oak-trees.png',
    deadTrees: '/mockup-tiles/overlay-dead-trees.png',
    mountain:  '/mockup-tiles/overlay-mountain-peak.png',
    crystal:   '/mockup-tiles/overlay-crystal-cluster.png',
    house:     '/mockup-tiles/overlay-small-house.png',
    ruinsPillar: '/mockup-tiles/overlay-ruins-pillar.png',
    campfire:  '/mockup-tiles/overlay-campfire.png',
    bridge:    '/mockup-tiles/overlay-wooden-bridge.png',
    chest:     '/mockup-tiles/overlay-treasure-chest.png',
    altar:     '/mockup-tiles/overlay-stone-altar.png',
    tower:     '/mockup-tiles/overlay-watchtower.png',
  },
};

// ─── Terrain Types ────────────────────────────────────────────────────────────

type TerrainType =
  | 'grass' | 'grass-dry' | 'dirt' | 'cobble'
  | 'water' | 'water-deep' | 'sand' | 'swamp' | 'snow'
  | 'lava' | 'ruins' | 'crystal'
  | 'forest-pine' | 'forest-oak' | 'forest-dead'
  | 'mountain' | 'crystal-cluster'
  | 'village' | 'village-tower' | 'camp'
  | 'ruins-altar' | 'treasure'
  | 'bridge'
  | 'void';

interface TerrainDef {
  label: string;
  baseTile: string;       // top-down base texture
  overlay?: string;       // optional feature overlay
  overlayScale?: number;  // how much the overlay extends beyond the hex (1.0 = fits hex, 1.4 = 40% larger)
  overlayOffsetY?: number; // vertical offset for overlay (negative = up)
  passable: boolean;
  color: string;
}

const T: Record<TerrainType, TerrainDef> = {
  'grass':           { label: 'Grass Plains',     baseTile: TILES.td.grass,     passable: true,  color: '#4a7c3f' },
  'grass-dry':       { label: 'Dry Grassland',    baseTile: TILES.td.grassDry,  passable: true,  color: '#6a7a3f' },
  'dirt':            { label: 'Dirt Path',         baseTile: TILES.td.dirt,      passable: true,  color: '#8b6b4a' },
  'cobble':          { label: 'Cobblestone Road',  baseTile: TILES.td.cobble,    passable: true,  color: '#6a6a6a' },
  'water':           { label: 'Shallow Water',     baseTile: TILES.td.water,     passable: true,  color: '#3a6e8f' },
  'water-deep':      { label: 'Deep Water',        baseTile: TILES.td.waterDeep, passable: false, color: '#1a3e5f' },
  'sand':            { label: 'Desert Sand',        baseTile: TILES.td.sand,     passable: true,  color: '#c4a44a' },
  'swamp':           { label: 'Murky Swamp',        baseTile: TILES.td.swamp,    passable: true,  color: '#3d5c3a' },
  'snow':            { label: 'Snow Field',          baseTile: TILES.td.snow,    passable: true,  color: '#c8d8e8' },
  'lava':            { label: 'Lava Field',          baseTile: TILES.td.lava,    passable: false, color: '#6a2a1a' },
  'ruins':           { label: 'Stone Ruins',         baseTile: TILES.td.ruins,   overlay: TILES.overlay.ruinsPillar, overlayScale: 1.2, overlayOffsetY: -10, passable: true,  color: '#6b5c7a' },
  'crystal':         { label: 'Crystal Floor',       baseTile: TILES.td.crystal, passable: true,  color: '#3a2a5a' },
  'forest-pine':     { label: 'Pine Forest',         baseTile: TILES.td.grass,   overlay: TILES.overlay.pines,    overlayScale: 1.5, overlayOffsetY: -18, passable: false, color: '#2a5a2a' },
  'forest-oak':      { label: 'Oak Forest',          baseTile: TILES.td.grass,   overlay: TILES.overlay.oaks,     overlayScale: 1.4, overlayOffsetY: -14, passable: false, color: '#3a6a2a' },
  'forest-dead':     { label: 'Dead Forest',         baseTile: TILES.td.swamp,   overlay: TILES.overlay.deadTrees, overlayScale: 1.3, overlayOffsetY: -12, passable: false, color: '#3a3a2a' },
  'mountain':        { label: 'Mountain',            baseTile: TILES.td.snow,    overlay: TILES.overlay.mountain,  overlayScale: 1.8, overlayOffsetY: -28, passable: false, color: '#5a4a6a' },
  'crystal-cluster': { label: 'Crystal Cave',        baseTile: TILES.td.crystal, overlay: TILES.overlay.crystal,   overlayScale: 1.3, overlayOffsetY: -10, passable: false, color: '#4a2a6a' },
  'village':         { label: 'Village',             baseTile: TILES.td.dirt,    overlay: TILES.overlay.house,     overlayScale: 1.2, overlayOffsetY: -12, passable: true,  color: '#8a7a5a' },
  'village-tower':   { label: 'Watchtower',          baseTile: TILES.td.cobble,  overlay: TILES.overlay.tower,     overlayScale: 1.4, overlayOffsetY: -20, passable: true,  color: '#7a7a7a' },
  'camp':            { label: 'Campfire',            baseTile: TILES.td.dirt,    overlay: TILES.overlay.campfire,   overlayScale: 0.8, overlayOffsetY: -4,  passable: true,  color: '#8a6a3a' },
  'ruins-altar':     { label: 'Mystic Altar',        baseTile: TILES.td.ruins,   overlay: TILES.overlay.altar,     overlayScale: 1.0, overlayOffsetY: -8,  passable: true,  color: '#5a4a7a' },
  'treasure':        { label: 'Treasure',            baseTile: TILES.td.ruins,   overlay: TILES.overlay.chest,     overlayScale: 0.7, overlayOffsetY: -2,  passable: true,  color: '#8a7a2a' },
  'bridge':          { label: 'Bridge',              baseTile: TILES.td.water,   overlay: TILES.overlay.bridge,    overlayScale: 1.0, overlayOffsetY: -4,  passable: true,  color: '#5a6a7a' },
  'void':            { label: '',                    baseTile: '',               passable: false, color: 'transparent' },
};

// ─── Map Data ─────────────────────────────────────────────────────────────────
// Single-char keys for compact map definition

const K: Record<string, TerrainType> = {
  'g': 'grass',       'G': 'grass-dry',    'p': 'dirt',        'c': 'cobble',
  'w': 'water',       'W': 'water-deep',   's': 'sand',        'S': 'swamp',
  'n': 'snow',        'L': 'lava',         'r': 'ruins',       'x': 'crystal',
  'F': 'forest-pine', 'O': 'forest-oak',   'D': 'forest-dead', 'M': 'mountain',
  'C': 'crystal-cluster', 'V': 'village',  'T': 'village-tower','K': 'camp',
  'A': 'ruins-altar', '$': 'treasure',     'B': 'bridge',      '_': 'void',
};

// Map: winding path from village (top-left) to crystal cave (bottom-right)
// Mountains, forests, lava form impassable maze walls
// Varied passable terrain creates interesting pathing
const MAP: string[][] = [
  ['_','M','M','F','_','M','M','_','_','F','M','M','_','_','_'],
  ['M','V','g','O','M','g','g','M','F','g','n','M','_','_','_'],
  ['_','g','p','g','M','p','g','F','g','p','n','F','M','_','_'],
  ['M','g','p','M','L','p','M','g','p','p','g','M','F','M','_'],
  ['_','M','p','g','L','p','G','g','p','M','w','w','g','O','_'],
  ['_','F','p','K','g','G','M','M','p','g','w','g','B','g','M'],
  ['_','_','M','g','S','p','g','g','p','M','w','g','g','p','g'],
  ['_','M','g','S','D','p','M','L','p','g','g','M','g','p','M'],
  ['_','_','M','g','S','g','L','L','G','p','p','g','s','p','_'],
  ['_','_','_','M','g','p','g','L','M','G','p','s','s','p','M'],
  ['_','_','_','_','M','p','c','g','r','A','p','s','p','g','_'],
  ['_','_','_','_','_','M','g','p','r','$','p','c','C','M','_'],
  ['_','_','_','_','_','_','M','M','F','M','M','M','M','_','_'],
];

const COLS = 15;
const ROWS = MAP.length;

// ─── Edge Detection ───────────────────────────────────────────────────────────
// Determine if a hex is on the perimeter (has void or out-of-bounds neighbors)

function isEdgeHex(col: number, row: number): boolean {
  // Flat-top hex neighbors
  const isOddCol = col % 2 === 1;
  const neighbors = [
    [col - 1, isOddCol ? row : row - 1], // top-left
    [col - 1, isOddCol ? row + 1 : row], // bottom-left
    [col + 1, isOddCol ? row : row - 1], // top-right
    [col + 1, isOddCol ? row + 1 : row], // bottom-right
    [col, row - 1],                       // top
    [col, row + 1],                       // bottom
  ];

  for (const [nc, nr] of neighbors) {
    if (nc < 0 || nc >= COLS || nr < 0 || nr >= ROWS) return true;
    const key = MAP[nr]?.[nc];
    if (!key || K[key] === 'void') return true;
  }
  return false;
}

// ─── Hex Geometry (flat-top) ──────────────────────────────────────────────────

const HEX_SIZE = 52;
const HEX_W = HEX_SIZE * 2;
const HEX_H = Math.sqrt(3) * HEX_SIZE;

function hexCenter(col: number, row: number): { x: number; y: number } {
  const x = col * (HEX_W * 0.75) + HEX_SIZE;
  const y = row * HEX_H + HEX_H / 2 + (col % 2 === 1 ? HEX_H / 2 : 0);
  return { x, y };
}

function hexPoints(cx: number, cy: number, size = HEX_SIZE): string {
  const pts: string[] = [];
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 180) * (60 * i);
    pts.push(`${cx + size * Math.cos(angle)},${cy + size * Math.sin(angle)}`);
  }
  return pts.join(' ');
}

// ─── Component ────────────────────────────────────────────────────────────────

interface HoveredHex {
  col: number;
  row: number;
  terrain: TerrainType;
}

export const HexMapMockup: React.FC = () => {
  const [hovered, setHovered] = useState<HoveredHex | null>(null);
  const [playerPos, setPlayerPos] = useState<{ col: number; row: number }>({ col: 1, row: 1 });

  // Extra padding for overlays that extend beyond hex bounds
  const PAD = 40;
  const gridW = COLS * (HEX_W * 0.75) + HEX_W * 0.25 + PAD * 2;
  const gridH = (ROWS + 0.5) * HEX_H + PAD * 2;

  const handleHexClick = useCallback((col: number, row: number, terrain: TerrainType) => {
    if (T[terrain].passable) {
      setPlayerPos({ col, row });
    }
  }, []);

  // Build render list: base layer first (all hexes), then overlays sorted back-to-front
  const hexes: Array<{ col: number; row: number; terrain: TerrainType; cx: number; cy: number; edge: boolean }> = [];
  for (let row = 0; row < ROWS; row++) {
    for (let col = 0; col < COLS; col++) {
      const key = MAP[row]?.[col] || '_';
      const terrain = K[key] || 'void';
      if (terrain === 'void') continue;
      const { x: cx, y: cy } = hexCenter(col, row);
      hexes.push({ col, row, terrain, cx: cx + PAD, cy: cy + PAD, edge: isEdgeHex(col, row) });
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0a1a 0%, #1a1a2e 50%, #0a0a1a 100%)',
      padding: '24px',
      fontFamily: '"Press Start 2P", "Courier New", monospace',
      overflow: 'auto',
    }}>
      {/* Title */}
      <div style={{ textAlign: 'center', marginBottom: '16px' }}>
        <h1 style={{
          color: '#d4af37',
          fontSize: '20px',
          textShadow: '2px 2px 0 #000, 0 0 10px rgba(212,175,55,0.3)',
          margin: 0,
          letterSpacing: '2px',
        }}>
          REALM OF SHADOWS
        </h1>
        <p style={{
          color: '#8a8a9a', fontSize: '10px', marginTop: '8px', letterSpacing: '1px',
        }}>
          Adventure Map v2 — Hybrid tiles with overlays
        </p>
      </div>

      {/* Compact legend */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'center',
        marginBottom: '16px', padding: '10px',
        background: 'rgba(0,0,0,0.4)', borderRadius: '8px',
        border: '1px solid rgba(212,175,55,0.2)',
        maxWidth: '1000px', margin: '0 auto 16px',
      }}>
        {(Object.entries(T) as [TerrainType, TerrainDef][])
          .filter(([key]) => key !== 'void')
          .map(([key, def]) => (
            <div key={key} style={{
              display: 'flex', alignItems: 'center', gap: '4px',
              padding: '3px 6px',
              background: def.passable ? 'rgba(74,124,63,0.15)' : 'rgba(106,42,26,0.15)',
              borderRadius: '3px',
              border: `1px solid ${def.passable ? 'rgba(74,124,63,0.3)' : 'rgba(106,42,26,0.3)'}`,
            }}>
              <div style={{
                width: '10px', height: '10px', borderRadius: '2px',
                backgroundColor: def.color,
              }} />
              <span style={{
                color: def.passable ? '#8fbf7a' : '#bf6a5a',
                fontSize: '7px',
              }}>
                {def.label}
              </span>
            </div>
          ))}
      </div>

      {/* Hex grid */}
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <div style={{
          position: 'relative',
          background: 'rgba(0,0,0,0.3)',
          borderRadius: '12px',
          border: '2px solid rgba(212,175,55,0.15)',
          padding: '0',
          boxShadow: '0 0 40px rgba(0,0,0,0.5), inset 0 0 40px rgba(0,0,0,0.3)',
          overflow: 'hidden',
        }}>
          <svg
            width={gridW}
            height={gridH}
            viewBox={`0 0 ${gridW} ${gridH}`}
            style={{ display: 'block' }}
          >
            <defs>
              <filter id="glow">
                <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              {/* Clip paths for all hexes */}
              {hexes.map(h => (
                <clipPath key={`clip-${h.col}-${h.row}`} id={`clip-${h.col}-${h.row}`}>
                  <polygon points={hexPoints(h.cx, h.cy)} />
                </clipPath>
              ))}
            </defs>

            {/* ── Layer 1: Base terrain tiles (rendered in row order) ── */}
            {hexes.map(h => {
              const def = T[h.terrain];
              const clipId = `clip-${h.col}-${h.row}`;
              // Choose iso edge tile for perimeter hexes, top-down for interior
              let baseSrc = def.baseTile;
              if (h.edge) {
                // Map terrain to appropriate edge tile variant
                if (h.terrain === 'lava' || h.terrain.includes('lava')) baseSrc = TILES.edge.lava;
                else if (h.terrain === 'snow' || h.terrain === 'mountain') baseSrc = TILES.edge.snow;
                else if (h.terrain === 'sand') baseSrc = TILES.edge.desert;
                else baseSrc = TILES.edge.grass;
              }
              const imgSize = HEX_SIZE * 2.2; // slightly oversized to ensure coverage
              return (
                <g key={`base-${h.col}-${h.row}`}>
                  {/* Fallback color fill */}
                  <polygon points={hexPoints(h.cx, h.cy)} fill={def.color} />
                  {/* Base tile clipped to hex */}
                  <image
                    href={baseSrc}
                    x={h.cx - imgSize / 2}
                    y={h.cy - imgSize / 2}
                    width={imgSize}
                    height={imgSize}
                    clipPath={`url(#${clipId})`}
                    style={{ imageRendering: 'pixelated' }}
                    preserveAspectRatio="xMidYMid slice"
                  />
                </g>
              );
            })}

            {/* ── Layer 2: Feature overlays (back-to-front for proper overlap) ── */}
            {hexes
              .filter(h => T[h.terrain].overlay)
              .sort((a, b) => a.cy - b.cy) // back-to-front: top rows render first
              .map(h => {
                const def = T[h.terrain];
                const scale = def.overlayScale || 1.0;
                const offsetY = def.overlayOffsetY || 0;
                const size = HEX_SIZE * 2 * scale;
                return (
                  <image
                    key={`overlay-${h.col}-${h.row}`}
                    href={def.overlay!}
                    x={h.cx - size / 2}
                    y={h.cy - size / 2 + offsetY}
                    width={size}
                    height={size}
                    style={{ imageRendering: 'pixelated' }}
                    preserveAspectRatio="xMidYMid meet"
                  />
                );
              })}

            {/* ── Layer 3: Hex borders and interaction ── */}
            {hexes.map(h => {
              const def = T[h.terrain];
              const isHov = hovered?.col === h.col && hovered?.row === h.row;
              const isPlayer = playerPos.col === h.col && playerPos.row === h.row;
              return (
                <g
                  key={`ui-${h.col}-${h.row}`}
                  onMouseEnter={() => setHovered({ col: h.col, row: h.row, terrain: h.terrain })}
                  onMouseLeave={() => setHovered(null)}
                  onClick={() => handleHexClick(h.col, h.row, h.terrain)}
                  style={{ cursor: def.passable ? 'pointer' : 'not-allowed' }}
                >
                  {/* Hex border */}
                  <polygon
                    points={hexPoints(h.cx, h.cy)}
                    fill={isHov && !def.passable ? 'rgba(255,40,20,0.08)' : 'none'}
                    stroke={
                      isPlayer ? '#ffd700'
                      : isHov ? (def.passable ? 'rgba(255,255,255,0.5)' : 'rgba(255,80,60,0.5)')
                      : 'rgba(0,0,0,0.25)'
                    }
                    strokeWidth={isPlayer ? 3 : isHov ? 2 : 0.5}
                  />
                  {/* Player marker */}
                  {isPlayer && (
                    <>
                      <circle
                        cx={h.cx} cy={h.cy} r={12}
                        fill="#ffd700" stroke="#fff" strokeWidth={2}
                        filter="url(#glow)" opacity={0.9}
                      />
                      <text
                        x={h.cx} y={h.cy + 1}
                        textAnchor="middle" dominantBaseline="central"
                        fontSize="14" fill="#1a1a2e" fontWeight="bold"
                        style={{ pointerEvents: 'none' }}
                      >
                        ★
                      </text>
                    </>
                  )}
                </g>
              );
            })}
          </svg>

          {/* Hover tooltip */}
          {hovered && hovered.terrain !== 'void' && (
            <div style={{
              position: 'absolute', bottom: '12px', left: '50%',
              transform: 'translateX(-50%)',
              background: 'rgba(0,0,0,0.9)',
              border: `1px solid ${T[hovered.terrain].passable ? '#4a7c3f' : '#8a3a2a'}`,
              borderRadius: '6px', padding: '8px 16px',
              display: 'flex', alignItems: 'center', gap: '10px',
              pointerEvents: 'none', zIndex: 10,
            }}>
              <div style={{
                width: '32px', height: '32px', borderRadius: '4px',
                backgroundColor: T[hovered.terrain].color,
              }} />
              <div>
                <div style={{ color: '#d4af37', fontSize: '10px', marginBottom: '2px' }}>
                  {T[hovered.terrain].label}
                </div>
                <div style={{
                  color: T[hovered.terrain].passable ? '#8fbf7a' : '#bf6a5a',
                  fontSize: '8px',
                }}>
                  {T[hovered.terrain].passable ? 'Passable terrain' : 'Impassable — find another way'}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HexMapMockup;
